import {CheckCircleFillIcon, CircleIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, IconButton, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../../constants/buttons'
import {LABELS} from '../../../constants/labels'
import {VALUES} from '../../../constants/values'
import {useQueryEditState, useQueryViewEditFields} from '../../../contexts/QueryContext'
import {
  SUPPORTED_VIEW_COLORS,
  VIEW_COLOR_BACKGROUND_MAP,
  VIEW_COLOR_FOREGROUND_MAP,
  VIEW_COLOR_LABELS,
} from '../../sidebar/ColorHelper'
import {CUSTOM_VIEW_ICONS_TO_PRIMER_ICON, customViewIconToPrimerIcon} from '../../sidebar/IconHelper'
import type {IconAndColorPickerViewFragment$key} from './__generated__/IconAndColorPickerViewFragment.graphql'
import styles from './IconAndColorPicker.module.css'

type Props = {
  readOnly: boolean
  currentView: IconAndColorPickerViewFragment$key
}

export function IconAndColorPicker({readOnly, currentView}: Props) {
  const {isEditing, isNewView} = useQueryEditState()

  const {icon: viewIcon, color: viewColor} = useFragment<IconAndColorPickerViewFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IconAndColorPickerViewFragment on Shortcutable {
        icon
        color
      }
    `,
    currentView,
  )

  const {dirtyViewIcon, setDirtyViewIcon, dirtyViewColor, setDirtyViewColor} = useQueryViewEditFields()
  const [isOpen, setIsOpen] = useState(false)

  const onSetOpen = useCallback((nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen)
  }, [])

  const onSave = useCallback(() => {
    onSetOpen(false)
  }, [onSetOpen])

  const onIconSelection = useCallback(
    (newIcon: string) => {
      setDirtyViewIcon(newIcon)
    },
    [setDirtyViewIcon],
  )

  const onColorSelection = useCallback(
    (newColor: string) => {
      setDirtyViewColor(newColor)
    },
    [setDirtyViewColor],
  )

  const onCancel = useCallback(() => {
    onSetOpen(false)
    setDirtyViewIcon(null)
    setDirtyViewColor(null)
  }, [onSetOpen, setDirtyViewIcon, setDirtyViewColor])

  const selectedIconName = dirtyViewIcon || (isNewView ? VALUES.defaultViewIcon : viewIcon)
  const selectedViewColor = dirtyViewColor || (isNewView ? VALUES.defaultViewColor : viewColor)
  const selectedIcon = customViewIconToPrimerIcon(selectedIconName)
  const selectedBackgroundColor = VIEW_COLOR_BACKGROUND_MAP[selectedViewColor]
  const selectedForegroundColor = VIEW_COLOR_FOREGROUND_MAP[selectedViewColor]

  function formatIconText(icon: string): string {
    if (!icon) return icon

    // Replace one or more underscores with a single space (GIT_PULL_REQUEST -> GIT PULL REQUEST)
    const withSpaces = icon.replace(/_+/g, ' ').trim()

    // Convert to lowercase and capitalize first character (GIT PULL REQUEST -> Git pull request)
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase()
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ViewIcon = useMemo(() => customViewIconToPrimerIcon(viewIcon)!, [viewIcon])

  return readOnly || !isEditing ? (
    <div
      style={{
        backgroundColor: selectedBackgroundColor,
        color: selectedForegroundColor,
      }}
      className={styles.iconDisplayContainer}
    >
      {/* eslint-disable-next-line react-hooks/static-components -- dynamic icon resolved from stable lookup map */}
      <ViewIcon />
    </div>
  ) : (
    <AnchoredOverlay
      renderAnchor={anchorProps => (
        <IconButton
          {...anchorProps}
          aria-labelledby={undefined}
          aria-label={LABELS.views.iconAndColorAnchorAriaLabel}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          icon={selectedIcon!}
          size="medium"
          variant="invisible"
          onClick={() => onSetOpen(!isOpen)}
          id="edit-view-icon-button"
          style={{
            backgroundColor: selectedBackgroundColor,
            color: selectedForegroundColor,
          }}
        />
      )}
      focusZoneSettings={{disabled: true}}
      focusTrapSettings={{restoreFocusOnCleanUp: true}}
      open={isOpen}
      onOpen={() => onSetOpen(true)}
      onClose={onCancel}
      className={styles.anchoredOverlay}
    >
      <div className={styles.overlayContent}>
        <span className={styles.sectionLabel}>{LABELS.views.color}</span>
        <div className={styles.colorSelectionGrid} data-testid="color-selection-grid">
          {SUPPORTED_VIEW_COLORS.map(color => {
            const fgColor = VIEW_COLOR_FOREGROUND_MAP[color]
            const bgColor = VIEW_COLOR_BACKGROUND_MAP[color]
            const isSelected = color === selectedViewColor
            const colorLabel = VIEW_COLOR_LABELS[color] ?? color

            return (
              <Tooltip text={`${colorLabel} color`} key={color} direction="n" delay="long">
                <IconButton
                  icon={isSelected ? CheckCircleFillIcon : CircleIcon}
                  aria-label={`${colorLabel} ${LABELS.views.color.toLowerCase()}`}
                  aria-checked={isSelected}
                  role="radio"
                  variant="invisible"
                  className={styles.colorPickerButton}
                  style={{
                    '--color-picker-fg': isSelected ? 'var(--fgColor-onEmphasis)' : fgColor,
                    '--color-picker-bg': isSelected ? fgColor : 'transparent',
                    '--color-picker-hover-fg': isSelected ? 'var(--fgColor-onEmphasis)' : fgColor,
                    '--color-picker-hover-bg': isSelected ? fgColor : bgColor,
                  }}
                  onClick={() => onColorSelection(color)}
                />
              </Tooltip>
            )
          })}
        </div>
        <span className={styles.sectionLabel}>{LABELS.views.icon}</span>
        <div className={styles.iconSelectionGrid} data-testid="icon-selection-grid">
          {Object.keys(CUSTOM_VIEW_ICONS_TO_PRIMER_ICON).map(icon => {
            const isSelected = icon === selectedIconName

            return (
              <Tooltip text={`${formatIconText(icon)} icon`} key={icon} direction="n" delay="long">
                <IconButton
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  icon={customViewIconToPrimerIcon(icon)!}
                  aria-label={`${formatIconText(icon)} ${LABELS.views.icon.toLowerCase()}`}
                  aria-checked={isSelected}
                  role="radio"
                  variant="invisible"
                  className={clsx(isSelected ? styles.iconPickerButtonSelected : styles.iconPickerButton)}
                  style={{
                    '--icon-picker-hover-bg': selectedBackgroundColor,
                    '--icon-picker-selected-bg': selectedForegroundColor,
                  }}
                  onClick={() => onIconSelection(icon)}
                />
              </Tooltip>
            )
          })}
        </div>
        <div className={styles.actionButtonsContainer}>
          <Button onClick={onCancel}>{BUTTON_LABELS.cancel}</Button>
          <Button variant="primary" onClick={onSave}>
            {BUTTON_LABELS.apply}
          </Button>
        </div>
      </div>
    </AnchoredOverlay>
  )
}
